import type { RunDetail, RunEvent } from '@/entities/sender.entity';
import { POLL_INTERVAL_MS, TERMINAL_STATUSES } from '@/pages/sender/sender-run-detail.constants';
import { useCallback, useEffect, useRef, useState } from 'react';

const RESUME_GRACE_MS = 120_000;

function mergeRunAfterFetch(
	runData: RunDetail,
	previous: RunDetail | null,
	resumeGraceUntil: number,
): RunDetail {
	if (
		runData.status === 'failed' &&
		previous?.status === 'queued' &&
		Date.now() < resumeGraceUntil
	) {
		return {
			...runData,
			status: 'queued',
			resumable: false,
			errorMessage: null,
		};
	}
	return runData;
}

type UseSenderRunDetailOptions = {
	enabled: boolean;
	loadRun: () => Promise<RunDetail>;
	loadEvents: () => Promise<RunEvent[]>;
};

export function useSenderRunDetail({ enabled, loadRun, loadEvents }: UseSenderRunDetailOptions) {
	const [run, setRun] = useState<RunDetail | null>(null);
	const [events, setEvents] = useState<RunEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const loadGenerationRef = useRef(0);
	const resumeGraceUntilRef = useRef(0);
	const runRef = useRef<RunDetail | null>(null);
	runRef.current = run;

	const loadData = useCallback(
		async (showSpinner = false) => {
			if (!enabled) return;
			const generation = ++loadGenerationRef.current;
			if (showSpinner) {
				setLoading(true);
				setError(false);
			}
			try {
				const [runData, eventsData] = await Promise.all([loadRun(), loadEvents()]);
				if (generation !== loadGenerationRef.current) return;
				setRun(prev => mergeRunAfterFetch(runData, prev, resumeGraceUntilRef.current));
				setEvents(eventsData);
				setError(false);
			} catch {
				if (generation !== loadGenerationRef.current) return;
				if (showSpinner || runRef.current === null) {
					setError(true);
				}
			} finally {
				if (showSpinner && generation === loadGenerationRef.current) {
					setLoading(false);
				}
			}
		},
		[enabled, loadRun, loadEvents],
	);

	useEffect(() => {
		if (!enabled) return;
		void loadData(true);
	}, [enabled, loadData]);

	useEffect(() => {
		if (!enabled || !run || TERMINAL_STATUSES.has(run.status)) return;

		const intervalId = window.setInterval(() => {
			void loadData();
		}, POLL_INTERVAL_MS);

		return () => window.clearInterval(intervalId);
	}, [enabled, run?.status, loadData]);

	const bumpGeneration = useCallback(() => {
		loadGenerationRef.current += 1;
	}, []);

	const applyOptimisticCancel = useCallback(() => {
		setRun(prev => (prev ? { ...prev, status: 'cancelled' } : prev));
	}, []);

	const applyOptimisticResume = useCallback(() => {
		resumeGraceUntilRef.current = Date.now() + RESUME_GRACE_MS;
		setRun(prev =>
			prev ? { ...prev, status: 'queued', resumable: false, errorMessage: null } : prev,
		);
	}, []);

	return {
		run,
		events,
		loading,
		error,
		loadData,
		bumpGeneration,
		applyOptimisticCancel,
		applyOptimisticResume,
	};
}
